import { useGetAccountQuery } from '@/services/accounts';
import { useFetchGeneratorAccountPricingMutation } from '@/services/pricing';
import { useGetCurrentDesignQuery } from '@/services/systemDesign';
import { Alert, Box, Button, RadioGroup, TextInput } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ChangeEvent, FocusEvent, useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { ProgramType } from 'types';

import FormSection from '../Forms/FormSection';
import LoadingOverlay from '../LoadingOverlay';
import { MainContainer } from '../MainContainer';

import { calculateGeneratorSystemDesignTotalCost } from './GeneratorQuoteCalculator.utils';
import { FormResultSection } from './HVACQuoteCalculator.components';

interface PricingFormValues {
  totalFinancedAmount: number | string;
  useCurrentSystemDesign: 'true' | 'false';
  term: string;
  rate: string;
}

export const GeneratorPricingCalculator = () => {
  const { monthlyPaymentCap = false } = useFlags();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const { data: account } = useGetAccountQuery(id);
  const programType = account?.programType || ProgramType.generator;
  const { data: systemDesign } = useGetCurrentDesignQuery({ id, programType }, { skip: !id || !account });
  const [trigger, { data = [], isLoading, status }] = useFetchGeneratorAccountPricingMutation();
  const systemDesignCost = useMemo(() => {
    if (!systemDesign) {
      return 0;
    }
    return calculateGeneratorSystemDesignTotalCost(systemDesign);
  }, [systemDesign]);

  const ratesAndTerms = useMemo(() => {
    if (!data || !data.length) {
      return { rates: [], terms: [] };
    }

    const rates = Array.from(new Set(data.map((item: any) => item.escalationRate)))
      .toSorted((a: any, b: any) => a - b)
      .map((rate: any) => ({
        id: rate.toString(),
        label: `${(rate * 100).toFixed(2)}%`,
        value: rate.toString(),
      }));
    const terms = Array.from(new Set(data.map((item: any) => item.term)))
      .toSorted((a: any, b: any) => a - b)
      .map((term: any) => ({
        id: term.toString(),
        label: `${term} years`,
        value: term.toString(),
      }));
    return { rates, terms };
  }, [data]);

  const pricingOptionsLimitedByMonthlyPaymentCap =
    monthlyPaymentCap && data.some((item: any) => item.resultsLimitedByMonthlyPaymentCap);

  const liveValues = useMemo<PricingFormValues>(
    () => ({
      totalFinancedAmount: systemDesignCost,
      useCurrentSystemDesign: systemDesign ? 'true' : 'false',
      term: '',
      rate: '',
    }),
    [systemDesignCost, systemDesign],
  );

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { isSubmitting },
  } = useForm<PricingFormValues>({
    defaultValues: liveValues,
    values: liveValues,
    resetOptions: { keepDirtyValues: true },
  });

  const onSubmit = useCallback(
    async (formValues: PricingFormValues) => {
      try {
        await trigger({
          accountId: id,
          totalFinancedAmount:
            formValues.useCurrentSystemDesign === 'false' ? Number(formValues.totalFinancedAmount) : undefined,
          useCurrentSystemDesign: formValues.useCurrentSystemDesign === 'true',
        });
      } catch (error) {
        console.error('Error fetching pricing:', error);
      }
    },
    [trigger, id],
  );

  const handleCancel = useCallback(() => {
    navigate(`/accounts/${id}`);
  }, [navigate, id]);

  const handleTotalFinancedAmountChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, fieldOnChange: (value: string) => void) => {
      const value = event.target.value;
      if (value.endsWith('.')) {
        fieldOnChange(value);
        return;
      }
      const numericValue = Number(value);
      if (!isNaN(numericValue)) {
        fieldOnChange(value);
        if (getValues('useCurrentSystemDesign') === 'true' && parseFloat(value) !== systemDesignCost) {
          setValue('useCurrentSystemDesign', 'false', { shouldDirty: true });
        }
      }
    },
    [getValues, setValue, systemDesignCost],
  );

  const handleTotalFinancedAmountBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>, fieldOnBlur: () => void, fieldOnChange: (value: number) => void) => {
      const value = parseFloat(event.target.value);
      fieldOnChange(isNaN(value) ? 0 : value);
      fieldOnBlur();
    },
    [],
  );

  const handleTotalFinancedAmountFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>, fieldOnChange: (value: string) => void) => {
      const value = parseFloat(event.target.value);
      if (value === 0) {
        fieldOnChange('');
      }
    },
    [],
  );

  const handleUseCurrentSystemDesignChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, fieldOnChange: (value: 'true' | 'false') => void) => {
      const selectedValue = event.target.value as 'true' | 'false';
      if (selectedValue === 'true') {
        setValue('totalFinancedAmount', systemDesignCost, { shouldDirty: true });
      }
      fieldOnChange(selectedValue);
    },
    [setValue, systemDesignCost],
  );

  return (
    <MainContainer>
      <Box position={'relative'}>
        {isLoading && <LoadingOverlay isVisible={true} />}

        <Box as="h2" fontWeight="medium" fontSize="lg" padding={{ base: 'lg lg 0 lg' }}>
          Payment Calculator
          <Box padding={{ base: 'xs 0 0 0' }}>
            {pricingOptionsLimitedByMonthlyPaymentCap && (
              <Alert
                variant="warning"
                message="Pricing options are currently limited based on the credit applicant or applicants (if there are co-applicants) on this account. Additional options may be available by adding a new co-applicant."
              ></Alert>
            )}
            <form onSubmit={handleSubmit(onSubmit)}>
              <FormSection
                title="Total Financed Amount"
                baseStyle={{
                  alignItems: {
                    base: 'flex-start',
                    tablet: 'flex-start',
                    desktop: 'center',
                  },
                  borderColor: 'transparent',
                  maxWidth: {
                    base: '100',
                    desktop: '100',
                  },
                }}
              >
                <Controller
                  name="totalFinancedAmount"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      id="totalFinancedAmount"
                      name="totalFinancedAmount"
                      label=""
                      hideLabel
                      prefix="$"
                      width="2xl"
                      value={field.value ?? ''}
                      onChange={(e) =>
                        handleTotalFinancedAmountChange(e as ChangeEvent<HTMLInputElement>, field.onChange)
                      }
                      onBlur={(e) => handleTotalFinancedAmountBlur(e, field.onBlur, field.onChange)}
                      onFocus={(e) => handleTotalFinancedAmountFocus(e, field.onChange)}
                    />
                  )}
                />
              </FormSection>
              {systemDesign ? (
                <FormSection title="Use Current System Design">
                  <Box
                    direction={{
                      base: 'column',
                      tablet: 'row',
                    }}
                    childGap={{ base: 'lg', desktop: 'xl' }}
                    style={{
                      maxWidth: '9.8em',
                    }}
                  >
                    <Controller
                      name="useCurrentSystemDesign"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup
                          name="useCurrentSystemDesign"
                          value={field.value}
                          onChange={(e) => handleUseCurrentSystemDesignChange(e, field.onChange)}
                          onBlur={field.onBlur}
                          options={[
                            { id: 'yes', label: 'Yes', value: 'true' },
                            { id: 'no', label: 'No', value: 'false' },
                          ]}
                        />
                      )}
                    />
                  </Box>
                </FormSection>
              ) : null}
              {!data.length && status !== 'uninitialized' && !isLoading ? (
                <Alert variant="info" message="No pricing options available for this account" />
              ) : null}
              {data?.length ? (
                <>
                  <FormSection title="Term (Contract Length)">
                    <Controller
                      name="term"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup
                          name="term"
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          onBlur={field.onBlur}
                          options={ratesAndTerms.terms}
                        />
                      )}
                    />
                  </FormSection>
                  <FormSection title="Select Escalator Rate">
                    <Controller
                      name="rate"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup
                          name="rate"
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          onBlur={field.onBlur}
                          options={ratesAndTerms.rates}
                        />
                      )}
                    />
                  </FormSection>
                </>
              ) : null}
              {data?.length ? <FormResultSection data={data} /> : null}
              <Box
                direction={{
                  base: 'column',
                  tablet: 'row',
                }}
                alignItems={{
                  base: 'stretch',
                  tablet: 'flex-end',
                }}
                justifyContent={{
                  tablet: 'flex-end',
                }}
                childGap="sm"
                style={{ flexShrink: 0 }}
                padding={{ base: 'lg', desktop: 'lg 0 0 0' }}
              >
                <Button
                  variant="secondary"
                  tone="neutral"
                  size="md"
                  onClick={handleCancel}
                  isDisabled={isLoading}
                  isLoading={isSubmitting}
                >
                  Cancel
                </Button>
                <Button size="md" variant="primary" type="submit" isLoading={isSubmitting}>
                  Fetch Pricing
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Box>
    </MainContainer>
  );
};
